import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type ManualInstallTarget = "iphone" | "ipad" | "mac";

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

/**
 * Plataformas Apple donde no existe el prompt nativo y la instalación es
 * manual. Ojo: iPadOS se presenta como "Macintosh" en el user agent; se lo
 * distingue por la pantalla táctil.
 */
function manualInstallTarget(): ManualInstallTarget | null {
  const agent = navigator.userAgent;
  if (/iphone|ipod/i.test(agent)) return "iphone";
  if (/ipad/i.test(agent) || (/macintosh/i.test(agent) && navigator.maxTouchPoints > 1)) return "ipad";

  const realSafari =
    /safari/i.test(agent) &&
    /version\//i.test(agent) &&
    !/(chrome|chromium|crios|fxios|edg|android)/i.test(agent);
  if (realSafari && /macintosh/i.test(agent)) return "mac";
  return null;
}

/**
 * Instalación de la PWA: en Android/desktop Chrome captura el prompt nativo
 * (el navegador no siempre lo sugiere solo); en Safari (iPhone/iPad/Mac) el
 * prompt no existe, así que expone la plataforma para mostrar instrucciones.
 */
export function usePwaInstall() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(function capturePwaInstallPrompt() {
    function handleBeforeInstall(event: Event) {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    }
    function handleInstalled() {
      setPromptEvent(null);
      setInstalled(true);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return function releasePwaInstallPrompt() {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === "accepted") setPromptEvent(null);
  }, [promptEvent]);

  return {
    canPrompt: promptEvent !== null && !installed,
    manualTarget: installed || promptEvent !== null ? null : manualInstallTarget(),
    install,
  };
}
